// MIGRATED TO CLOUD FUNCTIONS - API keys now secure on server-side
// All OpenAI API calls now go through Firebase Cloud Functions
import { createResponsesStream, uploadFile as uploadFileCloudFunction } from '../chat/openAIUtilsCloudFunctions';

// General assistant prompt ID for all grades and subjects
const GENERAL_ASSISTANT_PROMPT_ID = "pmpt_68afdbd73df08197b1b1c67917c780020ca441dd24b4d91a";

// Helper: turn the image_generation_call item into a plain object for UI
function extractImageFromItem(item) {
  if (item?.type === 'image_generation_call' &&
      item.status === 'completed' &&
      item.output_format === 'png' &&
      typeof item.result === 'string' &&
      item.result.length > 0) {
    return {
      kind: 'base64',
      mime: 'image/png',
      base64: item.result,
      revised_prompt: item.revised_prompt || ''
    };
  }
  return null;
}

/**
 * Enhanced streaming response handler with proper event handling
 * @param {string} userMessage - The user's message
 * @param {string} promptId - The prompt ID to use (optional)
 * @param {object} callbacks - Object containing callback functions
 * @param {string} conversationId - The conversation ID (optional)
 * @param {string[]} fileIds - Array of file IDs to include in the request (optional)
 * @returns {Promise<void>}
 */
export async function getStreamingAIResponse(userMessage, promptId = null, callbacks = {}, conversationId = null, fileIds = []) {
  const {
    onChunk = () => {},
    onStatus = () => {},
    onFileSearch = () => {},
    onImageGeneration = () => {},
    onImageGenerated = () => {},
    onReasoning = () => {},
    onError = () => {},
    onComplete = () => {}
  } = callbacks;

  // Retry configuration
  const maxRetries = 3;
  const baseDelay = 1000; // 1 second

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
    // Use the General Assistant prompt ID for AFA Curriculum Assistant if none provided
    const defaultPromptId = GENERAL_ASSISTANT_PROMPT_ID;
    
    const requestBody = {
      prompt: {
        "id": promptId || defaultPromptId,
        "version": "2"
      },
      input: [
        {
          role: "user",
          content: userMessage
        }
      ],
      stream: true,
      text: {
        "format": {
          "type": "text"
        },
        "verbosity": "medium"
      },
      reasoning: {
        "effort": "low",
        "summary": "auto"
      },
      tools: [
        {
          "type": "file_search",
          "vector_store_ids": [
            "vs_U2HA0JbAh7LHQ357k5EmYJvO"
          ]
        },
        {
            "type": "code_interpreter",
            "container": {
              "type": "auto",
              "file_ids": fileIds
            }
        },
        {
          "type": "image_generation",
          "background": "auto",
          "moderation": "auto",
          "output_compression": 100,
          "output_format": "png",
          "quality": "auto",
          "size": "auto"
        }
      ],
      store: true,
      include: [
        "code_interpreter_call.outputs",
        "reasoning.encrypted_content",
        "web_search_call.action.sources"
      ]
    };

    // Add previous response ID if provided to maintain conversation context
    if (conversationId) {
      requestBody.previous_response_id = conversationId;
    }

    // Use Cloud Functions for streaming Responses API.
    // The Firebase function timeout is the outer guard for stalled requests.
    const streamPromise = createResponsesStream(
      requestBody,
      'Nur Al Huda', // assistantTitle
      null, // resourceId
      {
        onChunk,
        onStatus,
        onFileSearch,
        onImageGeneration,
        onImageGenerated: (item) => {
          const image = extractImageFromItem(item);
          if (image) {
            onImageGenerated(image);
          }
        },
        onReasoning,
        onError,
        onComplete,
      }
    );
    
    // Stream handling is now done via callbacks in createResponsesStream
    // The Cloud Function will call our callbacks as events come in
    await streamPromise;
    // If we get here, the request was successful
    break;
    } catch (error) {
      console.error(`Attempt ${attempt}/${maxRetries} failed:`, error);
      
      // Check if it's a retryable error
      const isRetryableError = 
        // Network errors
        error.message?.includes('network error') || 
        error.message?.includes('QUIC') ||
        error.message?.includes('fetch') ||
        error.code === 'NETWORK_ERROR' ||
        // Gateway errors (502 Bad Gateway, 504 Gateway Timeout) - often transient
        error.status === 502 ||
        error.status === 504 ||
        // Explicitly marked as retryable
        error.retryable === true;
      
      if (attempt < maxRetries && isRetryableError) {
        const delay = baseDelay * Math.pow(2, attempt - 1); // Exponential backoff
        console.log(`Retrying in ${delay}ms... (Error: ${error.status || error.message})`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      
      // If we've exhausted retries or it's not a retryable error, throw
      console.error('All retry attempts failed or non-retryable error:', error);
      onError(error);
      return;
    }
  }
}

/**
 * Get streaming response with specific context for curriculum development
 * @param {string} userMessage - The user's message
 * @param {object} context - Additional context (grade, subject, etc.)
 * @param {object} callbacks - Callback functions for different events
 * @param {string} conversationId - The conversation ID (optional)
 * @param {string[]} fileIds - Array of file IDs to include (optional)
 * @returns {Promise<void>}
 */
export async function getStreamingCurriculumResponse(userMessage, context = {}, callbacks = {}, conversationId = null, fileIds = []) {
  try {
    // The prompt ID handles all the system context, just pass the user message directly
    await getStreamingAIResponse(userMessage, GENERAL_ASSISTANT_PROMPT_ID, callbacks, conversationId, fileIds);
  } catch (error) {
    console.error('Error getting streaming curriculum response:', error);
    if (callbacks.onError) {
      callbacks.onError(error);
    }
  }
}

/**
 * Simple function to get response from OpenAI using the Responses API (non-streaming)
 * @param {string} userMessage - The user's message
 * @param {string} promptId - The prompt ID to use (optional, will use default if not provided)
 * @param {string} conversationId - The conversation ID (optional)
 * @returns {Promise<string>} The AI response
 */
export async function getAIResponse(userMessage, promptId = null, conversationId = null) {
  try {
    // Use the General Assistant prompt ID for AFA Curriculum Assistant if none provided
    const defaultPromptId = GENERAL_ASSISTANT_PROMPT_ID;
    
    const requestBody = {
      prompt: {
        "id": promptId || defaultPromptId,
        "version": "2"
      },
      input: [
        {
          role: "user",
          content: userMessage
        }
      ],
      text: {
        "format": {
          "type": "text"
        },
        "verbosity": "medium"
      },
      reasoning: {
        "effort": "low",
        "summary": "auto"
      },
      tools: [
        {
          "type": "file_search",
          "vector_store_ids": [
            "vs_68af4b97162481918718f175c7d3ac48"
          ]
        },
        {
          "type": "image_generation",
          "background": "auto",
          "moderation": "auto",
          "output_compression": 100,
          "output_format": "png",
          "quality": "auto",
          "size": "auto"
        }
      ],
      store: true,
      include: [
        "reasoning.encrypted_content",
        "web_search_call.action.sources"
      ]
    };

    // Add previous response ID if provided to maintain conversation context
    if (conversationId) {
      requestBody.previous_response_id = conversationId;
    }

    // Use Cloud Functions for non-streaming Responses API
    // Note: This needs to be added to Cloud Functions if not already supported
    // For now, we'll use the streaming version with a wrapper
    throw new Error('Non-streaming Responses API not yet implemented via Cloud Functions. Use getStreamingAIResponse instead.');
  } catch (error) {
    console.error('Error calling OpenAI API:', error);
    
    // Return a helpful error message
    if (error.code === 'insufficient_quota') {
      return "I'm currently experiencing high usage. Please try again in a moment.";
    } else if (error.code === 'invalid_api_key') {
      return "There's an issue with the API configuration. Please check your settings.";
    } else {
      return "I encountered an error while processing your request. Please try again.";
    }
  }
}

/**
 * Get response with specific context for curriculum development
 * @param {string} userMessage - The user's message
 * @param {object} context - Additional context (grade, subject, etc.)
 * @returns {Promise<string>} The AI response
 */
export async function getCurriculumResponse(userMessage, context = {}) {
  try {
    // The prompt ID handles all the system context, just pass the user message directly
    return await getAIResponse(userMessage);
  } catch (error) {
    console.error('Error getting curriculum response:', error);
    return "I encountered an error while processing your curriculum request. Please try again.";
  }
}

/**
 * Upload a file to OpenAI for use with the code interpreter
 * @param {File} file - The file to upload
 * @returns {Promise<string>} The file ID
 */
export async function uploadFile(file) {
  try {
    // Use Cloud Functions for secure file uploads
    const result = await uploadFileCloudFunction(file, 'Nur Al Huda', null, 'assistants');
    return result.id;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
}

/**
 * Upload multiple files and return their IDs
 * @param {File[]} files - Array of files to upload
 * @returns {Promise<string[]>} Array of file IDs
 */
export async function uploadFiles(files) {
  const results = await Promise.allSettled(files.map(file => uploadFile(file)));
  // Return null for failed uploads; caller checks for null
  return results.map(r => (r.status === 'fulfilled' ? r.value : null));
}

/**
 * Check if the API key is configured
 * @returns {boolean} True if API key is available
 */
export function isApiKeyConfigured() {
  // API keys are now in Cloud Functions, so we assume they're configured
  // if the user is authenticated (required for Cloud Functions)
  return true; // Cloud Functions handle API key validation
}

/**
 * Get a demo response for testing (when API is not available)
 * @param {string} userMessage - The user's message
 * @returns {string} A demo response
 */
export function getDemoResponse(userMessage) {
  const lowerMessage = userMessage.toLowerCase();
  
  if (lowerMessage.includes('lesson plan') || lowerMessage.includes('curriculum')) {
    return `# Demo Lesson Plan Response

## Grade 2 Science: Animal Adaptations

### Learning Objectives:
- Students will understand how animals adapt to their environments
- Students will identify different types of adaptations
- Students will connect adaptations to survival needs

### Standards Alignment:
- Virginia SOL 2.4: The student will investigate and understand that plants and animals undergo a series of orderly changes as they grow and develop
- Islamic Studies: Appreciation for Allah's creation and the wisdom in animal design

### Lesson Structure (45 minutes):
1. **Hook (5 min)**: Show pictures of different animals in their habitats
2. **Mini-lesson (15 min)**: Discuss what adaptations are and why animals need them
3. **Guided Practice (15 min)**: Students work in pairs to identify adaptations
4. **Independent Practice (8 min)**: Individual worksheet on adaptations
5. **Exit Ticket (2 min)**: Quick assessment

### Activities:
- Adaptation matching game
- Habitat exploration activity
- Islamic reflection on Allah's wisdom in creation

*This is a demo response. For full functionality, please configure your OpenAI API key.*`;
  }
  
  if (lowerMessage.includes('islamic') || lowerMessage.includes('islam')) {
    return `# Islamic Studies Integration

## Connecting Faith with Learning

### Key Principles:
- All creation reflects Allah's wisdom and design
- Learning about the world helps us appreciate Allah's creation
- Science and faith complement each other

### Integration Ideas:
- Begin lessons with a dua for seeking knowledge
- Connect scientific concepts to Islamic teachings
- Encourage gratitude for Allah's blessings
- Use Islamic stories and examples when relevant

### Example Integration:
When teaching about animal adaptations, discuss how Allah has designed each animal perfectly for its environment, showing His wisdom and care for all creation.

*This is a demo response. For full functionality, please configure your OpenAI API key.*`;
  }
  
  if (lowerMessage.includes('image') || lowerMessage.includes('picture') || lowerMessage.includes('visual')) {
    return `# Image Generation Demo

I can help you create images for your curriculum! Here are some examples of what I can generate:

## Educational Images:
- **Science Diagrams**: Animal adaptations, plant life cycles, weather patterns
- **Islamic Art**: Geometric patterns, calligraphy, mosque architecture
- **Math Visuals**: Fractions, shapes, number lines
- **Social Studies**: Maps, historical scenes, community helpers

## Example Prompts:
- "Create an image of a Grade 2 classroom with Islamic art elements"
- "Generate a diagram showing animal adaptations for desert life"
- "Make a visual representation of Islamic geometric patterns for math class"

*This is a demo response. For actual image generation, please configure your OpenAI API key.*`;
  }
  
  if (lowerMessage.includes('search') || lowerMessage.includes('find') || lowerMessage.includes('document')) {
    return `# File Search Demo

I can search through your curriculum documents and resources! Here's what I can help you find:

## Search Capabilities:
- **Curriculum Standards**: Virginia SOL, Islamic Studies frameworks
- **Lesson Plans**: Existing lesson templates and examples
- **Assessment Materials**: Quizzes, rubrics, evaluation tools
- **Resources**: Worksheets, activities, multimedia content

## Example Searches:
- "Find Grade 2 Science standards about animal adaptations"
- "Search for Islamic studies integration examples"
- "Look for assessment strategies for math lessons"

*This is a demo response. For actual file search, please configure your OpenAI API key.*`;
  }
  
  return `Thank you for your question about "${userMessage}". I'm here to help you with curriculum development, lesson planning, and educational content creation.

Currently, I'm running in demo mode. To get full AI-powered responses, please configure your OpenAI API key in the environment variables.

I can help you with:
- Creating lesson plans
- Integrating Islamic studies
- Finding curriculum standards
- Assessment strategies
- Educational activities
- **File search** through your documents
- **Image generation** for visual aids

What would you like to work on?`;
}

/**
 * Get a streaming demo response for testing (when API is not available)
 * @param {string} userMessage - The user's message
 * @param {object} callbacks - Callback functions for different events
 * @returns {Promise<void>}
 */
export async function getStreamingDemoResponse(userMessage, callbacks = {}) {
  const { onChunk = () => {}, onStatus = () => {}, onComplete = () => {} } = callbacks;
  
  const demoResponse = getDemoResponse(userMessage);
  
  // Simulate streaming by sending the response in chunks
  const words = demoResponse.split(' ');
  const chunkSize = 3; // Send 3 words at a time
  
  onStatus('created', { status: 'created' });
  onStatus('in_progress', { status: 'in_progress' });
  
  for (let i = 0; i < words.length; i += chunkSize) {
    const chunk = words.slice(i, i + chunkSize).join(' ');
    onChunk(chunk + (i + chunkSize < words.length ? ' ' : ''));
    
    // Add a small delay to simulate real streaming
    await new Promise(resolve => setTimeout(resolve, 50));
  }
  
  onStatus('completed', { status: 'completed' });
  onComplete({ status: 'completed' });
}

/**
 * Generate an image using OpenAI's image generation
 * @param {string} prompt - The image generation prompt
 * @param {object} options - Image generation options
 * @returns {Promise<string>} The generated image URL or base64 data
 */
export async function generateImage(prompt, options = {}) {
  // MIGRATED: Image generation is now handled via Responses API with image_generation tool
  // This function is deprecated - use getStreamingAIResponse with image_generation tool instead
  throw new Error('generateImage is deprecated. Use getStreamingAIResponse with image_generation tool via Cloud Functions instead.');
}

/**
 * Search through files using OpenAI's file search
 * @param {string} query - The search query
 * @param {object} options - Search options
 * @returns {Promise<object>} Search results
 */
export async function searchFiles(query, options = {}) {
  try {
    // This would typically use OpenAI's file search API
    // For now, we'll return a mock response
    return {
      results: [
        {
          file: "curriculum_standards.pdf",
          content: `Found content related to: ${query}`,
          relevance: 0.95
        }
      ],
      query: query
    };
  } catch (error) {
    console.error('Error searching files:', error);
    throw error;
  }
}
